import { useCallback, useEffect, useState } from 'react'
import { Shield, X, Eye, EyeOff, Loader2, Trash2, ToggleLeft, ToggleRight, KeyRound } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '../../auth/store/authStore'
import { platformAdminService } from '../../../services/platformAdminService'
import { platformPasswordResetService } from '../../../services/platformPasswordResetService'
import type { PlatformAdmin } from '../../../types/api.types'
import { LoadErrorState } from '../../../components/LoadErrorState'
import { EmptyState } from '../../admin/components/EmptyState'
import { Tooltip } from '../../../components/Tooltip'

interface AdminFormState {
  name: string
  email: string
  phone: string
  password: string
}

const EMPTY_FORM: AdminFormState = { name: '', email: '', phone: '', password: '' }

interface FormErrors {
  name?: string
  email?: string
  phone?: string
  password?: string
}

export function PlatformAdminsTab(): JSX.Element {
  const authUser = useAuthStore((s) => s.user)

  const [admins, setAdmins] = useState<PlatformAdmin[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<AdminFormState>(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Reset password modal state
  const [resetTarget, setResetTarget] = useState<PlatformAdmin | null>(null)
  const [resetPwd, setResetPwd] = useState('')
  const [resetPwdVisible, setResetPwdVisible] = useState(false)
  const [resetForceLogout, setResetForceLogout] = useState(true)
  const [resetPwdError, setResetPwdError] = useState<string | undefined>()
  const [resetSubmitting, setResetSubmitting] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await platformAdminService.getAll()
      setAdmins(data)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  function resetForm() {
    setForm(EMPTY_FORM)
    setFormErrors({})
    setShowPassword(false)
  }

  function validateForm(): boolean {
    const errors: FormErrors = {}
    if (!form.name.trim()) errors.name = 'Name is required'
    if (!form.email.trim() || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      errors.email = 'Valid email is required'
    }
    if (form.phone) {
      const digits = form.phone.replace(/[\s\-()+]/g, '')
      if (digits.length < 7 || digits.length > 15) errors.phone = 'Phone must be 7–15 digits'
    }
    if (!form.password) {
      errors.password = 'Password is required'
    } else if (form.password.length < 8 || !/[a-zA-Z]/.test(form.password) || !/[0-9]/.test(form.password)) {
      errors.password = 'Password must be 8+ characters with letters and numbers'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleCreate() {
    if (!validateForm()) return
    setSubmitting(true)
    try {
      const newAdmin = await platformAdminService.create({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || undefined,
        organization_id: authUser?.organization_id ?? 1,
      })
      toast.success(`Platform admin '${newAdmin.name}' created. Credentials sent to ${newAdmin.email}.`)
      setShowModal(false)
      resetForm()
      await loadData()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to create admin'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleToggleStatus(admin: PlatformAdmin) {
    try {
      await platformAdminService.toggleStatus(admin.id, !admin.is_active)
      toast.success(`Admin ${admin.is_active ? 'deactivated' : 'activated'} successfully`)
      await loadData()
    } catch {
      toast.error('Failed to update admin status')
    }
  }

  async function handleDelete(admin: PlatformAdmin) {
    if (!window.confirm(`Delete platform admin "${admin.name}"? This cannot be undone.`)) return
    try {
      await platformAdminService.delete(admin.id)
      toast.success('Platform admin deleted')
      await loadData()
    } catch {
      toast.error('Failed to delete admin')
    }
  }

  function openResetModal(admin: PlatformAdmin) {
    setResetTarget(admin)
    setResetPwd('')
    setResetPwdVisible(false)
    setResetForceLogout(true)
    setResetPwdError(undefined)
  }

  async function handleResetPassword() {
    if (!resetTarget) return
    if (!resetPwd || resetPwd.length < 8 || !/[a-zA-Z]/.test(resetPwd) || !/[0-9]/.test(resetPwd)) {
      setResetPwdError('At least 8 characters with letters and numbers')
      return
    }
    if (resetTarget.id === authUser?.id) {
      toast.error('Use "Change Password" in your profile to reset your own password.')
      return
    }
    setResetSubmitting(true)
    try {
      const res = await platformPasswordResetService.resetPlatformAdmin(resetTarget.id, {
        new_password: resetPwd,
        force_logout: resetForceLogout,
      })
      toast.success(
        res.email
          ? `Password reset for '${resetTarget.name}'. Notification sent to ${res.email}.`
          : `Password reset for '${resetTarget.name}'. No email on file — share credentials manually.`,
      )
      setResetTarget(null)
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Reset failed')
    } finally {
      setResetSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-brand-green" />
        <span className="ml-2 text-sm text-gray-500">Loading platform admins...</span>
      </div>
    )
  }

  if (loadError) {
    return <LoadErrorState message="Couldn't load platform admins" onRetry={loadData} />
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Platform admins have full cross-tenant access to all organisations and data.
        </p>
        <button
          type="button"
          onClick={() => { resetForm(); setShowModal(true) }}
          className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-70"
        >
          + Add Platform Admin
        </button>
      </div>

      {admins.length === 0 ? (
        <EmptyState icon={Shield} message="No platform admins" hint="Add the first platform admin above" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="px-6 py-3 text-left text-[13px] font-medium text-gray-500">Name</th>
                <th className="px-6 py-3 text-left text-[13px] font-medium text-gray-500">Email</th>
                <th className="px-6 py-3 text-left text-[13px] font-medium text-gray-500">Phone</th>
                <th className="px-6 py-3 text-left text-[13px] font-medium text-gray-500">Organisation</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Status</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Created</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.id} className="border-t border-gray-100">
                  <td className="px-6 py-4 text-[14px] font-medium text-gray-900">{admin.name}</td>
                  <td className="px-6 py-4 text-[14px] text-brand-green">{admin.email}</td>
                  <td className="px-6 py-4 text-[14px] text-gray-700">{admin.phone || '—'}</td>
                  <td className="px-6 py-4 text-[14px] text-gray-700">{admin.organization_name || '—'}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[12px] font-medium ${
                      admin.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {admin.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center text-[13px] text-gray-500">
                    {new Date(admin.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <Tooltip label={admin.is_active ? 'Deactivate' : 'Activate'}>
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(admin)}
                          className={admin.is_active ? 'text-brand-green hover:text-brand-green/70' : 'text-gray-400 hover:text-gray-600'}
                        >
                          {admin.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        </button>
                      </Tooltip>
                      <Tooltip label="Reset password">
                        <button
                          type="button"
                          onClick={() => openResetModal(admin)}
                          className="text-amber-500 hover:text-amber-600"
                        >
                          <KeyRound size={15} />
                        </button>
                      </Tooltip>
                      <Tooltip label="Delete admin">
                        <button
                          type="button"
                          onClick={() => handleDelete(admin)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Add Platform Admin</h2>
              <button type="button" onClick={() => { setShowModal(false); resetForm() }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Full Name*</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => { setForm({ ...form, name: e.target.value }); if (formErrors.name) setFormErrors(p => ({ ...p, name: undefined })) }}
                  className={`w-full rounded-lg border px-3 py-2.5 text-[14px] outline-none focus:border-brand-green ${formErrors.name ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="Enter full name"
                />
                {formErrors.name && <p className="mt-1 text-[12px] text-red-500">{formErrors.name}</p>}
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Email*</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => { setForm({ ...form, email: e.target.value }); if (formErrors.email) setFormErrors(p => ({ ...p, email: undefined })) }}
                  className={`w-full rounded-lg border px-3 py-2.5 text-[14px] outline-none focus:border-brand-green ${formErrors.email ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="admin@platform.com"
                />
                {formErrors.email && <p className="mt-1 text-[12px] text-red-500">{formErrors.email}</p>}
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Phone (optional)</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => { setForm({ ...form, phone: e.target.value }); if (formErrors.phone) setFormErrors(p => ({ ...p, phone: undefined })) }}
                  className={`w-full rounded-lg border px-3 py-2.5 text-[14px] outline-none focus:border-brand-green ${formErrors.phone ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="+923001234567"
                />
                {formErrors.phone && <p className="mt-1 text-[12px] text-red-500">{formErrors.phone}</p>}
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Password*</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => { setForm({ ...form, password: e.target.value }); if (formErrors.password) setFormErrors(p => ({ ...p, password: undefined })) }}
                    className={`w-full rounded-lg border py-2.5 pl-3 pr-10 text-[14px] outline-none focus:border-brand-green ${formErrors.password ? 'border-red-400' : 'border-gray-200'}`}
                    placeholder="Min. 8 chars with letters & numbers"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {formErrors.password && <p className="mt-1 text-[12px] text-red-500">{formErrors.password}</p>}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={handleCreate}
                disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Creating...' : 'Create Admin'}
              </button>
              <button
                type="button"
                onClick={() => { setShowModal(false); resetForm() }}
                className="flex-1 rounded-lg border border-gray-300 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-[15px] font-bold text-gray-900">Reset Password</h2>
                <p className="text-[12px] text-gray-500">{resetTarget.name} · {resetTarget.email}</p>
              </div>
              <button type="button" onClick={() => setResetTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            {resetTarget.id === authUser?.id && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
                Use "Change Password" in your profile to reset your own password.
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">New Password*</label>
                <div className="relative">
                  <input
                    type={resetPwdVisible ? 'text' : 'password'}
                    value={resetPwd}
                    onChange={(e) => { setResetPwd(e.target.value); if (resetPwdError) setResetPwdError(undefined) }}
                    className={`w-full rounded-lg border py-2.5 pl-3 pr-10 text-[14px] outline-none focus:border-brand-green ${resetPwdError ? 'border-red-400' : 'border-gray-200'}`}
                    placeholder="Min. 8 chars with letters & numbers"
                  />
                  <button type="button" tabIndex={-1} onClick={() => setResetPwdVisible(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {resetPwdVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {resetPwdError && <p className="mt-1 text-[12px] text-red-500">{resetPwdError}</p>}
              </div>

              <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-gray-200 p-3 text-[13px] text-gray-700 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={resetForceLogout}
                  onChange={(e) => setResetForceLogout(e.target.checked)}
                  className="accent-brand-green"
                />
                <span>
                  Force logout <span className="text-gray-400">(invalidate session immediately)</span>
                </span>
              </label>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={resetSubmitting || resetTarget.id === authUser?.id}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                {resetSubmitting ? 'Resetting…' : 'Reset Password'}
              </button>
              <button
                type="button"
                onClick={() => setResetTarget(null)}
                className="flex-1 rounded-lg border border-gray-300 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
